/*
 * LAOR Cart Gifts — keeps the gift lines in the cart in step with the units of the counted products,
 * and adds one more unit from the drawer's nudge. The panel itself is Liquid (snippets/laor-cart-gifts.liquid),
 * re-rendered by the drawer on every cart change.
 *
 * Settling: after every cart change (PUB_SUB_EVENTS.cartUpdate, cart:update) it reads the cart the event
 * carries (or SugarCart's), counts the units (lines of the counted handles that no block added on the
 * shopper's behalf), and for each gift: adds one line when earned and missing, removes it when no longer
 * earned, trims duplicates. One /cart/update.js and one /cart/add.js at most, then one announcement so the
 * drawer repaints; a settled cart makes no request, so the announcement can't loop. The gift lines carry
 * the same properties as Sugar's Gift Unlocks (_gift_unlock, _gift_for_product, _gift_unlock_required_qty).
 *
 * On a page where Sugar's Gift Unlocks sells the same gift variants (the product page), Sugar settles the
 * cart and this script stands aside, so the two never act on the same line.
 */
(function () {
  'use strict';
  if (window.LaorCartGifts) return;

  const ROOT = '[data-laor-cart-gifts]';
  const AUTO_PROPS = ['_gift_unlock', '_gift_list', '_gift_picker', '_offer_gift', '_sugar_progress_goal', '_cart_toggle', '_cart_upsell'];
  let busy = false;
  let dirty = false;
  let timer = null;
  let pending = null;
  const recent = [];

  function isCart(value) {
    return !!(value && Array.isArray(value.items) && typeof value.item_count === 'number');
  }

  function cartOf(detail) {
    if (isCart(detail)) return detail;
    if (detail && isCart(detail.cart)) return detail.cart;
    if (detail && isCart(detail.cartData)) return detail.cartData;
    return null;
  }

  function isAuto(item) {
    const p = item.properties || {};
    return AUTO_PROPS.some((key) => p[key] !== undefined && p[key] !== null && p[key] !== '' && p[key] !== false);
  }

  function config() {
    const root = document.querySelector(ROOT);
    const el = root && root.querySelector('[data-lcg-config]');
    if (!el) return null;
    try {
      const cfg = JSON.parse(el.textContent || '{}');
      return Array.isArray(cfg.gifts) && cfg.gifts.length ? cfg : null;
    } catch (e) {
      return null;
    }
  }

  function sectionIds() {
    if (typeof window.sugarCartSectionIds === 'function') return window.sugarCartSectionIds();
    return ['sugar-cart-drawer', 'cart-icon-bubble'];
  }

  /* Sugar's Gift Unlocks on this page already sells one of these gifts: let it settle the cart. */
  function sugarSettles(cfg) {
    return cfg.gifts.some((gift) =>
      Array.prototype.some.call(document.querySelectorAll('[data-gift-unlocks] [data-gift-unlock][data-variant-id="' + gift.id + '"]'),
        (el) => !el.closest('sugar-cart-drawer'))
    );
  }

  async function post(url, body) {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(Object.assign({ sections: sectionIds(), sections_url: window.location.pathname }, body)),
    });
    const data = await res.json();
    if (data && data.status) throw new Error(data.description || data.message || 'Cart error');
    return data;
  }

  async function announce(result) {
    const cartData = window.SugarCart ? await window.SugarCart.get({ fresh: true }) : cartOf(result);
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'laor-cart-gifts', cartData, sections: result && result.sections });
    }
    document.dispatchEvent(new CustomEvent('cart:update', { detail: result }));
  }

  /* Never more than three settling rounds that changed the cart in ten seconds */
  function allowed() {
    const now = Date.now();
    while (recent.length && now - recent[0] > 10000) recent.shift();
    return recent.length < 3;
  }

  async function settle(cart) {
    const cfg = config();
    if (!cfg || sugarSettles(cfg)) return;
    if (busy) {
      dirty = true;
      return;
    }
    if (!isCart(cart)) {
      cart = window.SugarCart ? await window.SugarCart.get() : null;
      if (!isCart(cart)) return;
    }

    const handles = new Set(cfg.handles || []);
    let units = 0;
    let productId = '';
    cart.items.forEach((item) => {
      if (isAuto(item) || !handles.has(item.handle)) return;
      units += parseInt(item.quantity, 10) || 0;
      if (!productId) productId = String(item.product_id);
    });

    const updates = {};
    const additions = [];
    cfg.gifts.forEach((gift) => {
      const lines = cart.items.filter((item) => String(item.variant_id) === String(gift.id) && item.properties && item.properties._gift_unlock);
      const earned = units >= gift.qty;
      if (!earned) {
        lines.forEach((line) => { updates[line.key] = 0; });
        return;
      }
      if (!lines.length) {
        additions.push({ id: gift.id, quantity: 1, properties: { _gift_unlock: 'true', _gift_for_product: productId, _gift_unlock_required_qty: String(gift.qty) } });
        return;
      }
      lines.forEach((line, i) => {
        const want = i === 0 ? 1 : 0;
        if (parseInt(line.quantity, 10) !== want) updates[line.key] = want;
      });
    });

    if (!Object.keys(updates).length && !additions.length) return;
    if (!allowed()) return;
    recent.push(Date.now());

    busy = true;
    try {
      let result = null;
      if (Object.keys(updates).length) result = await post('/cart/update.js', { updates });
      if (additions.length) result = await post('/cart/add.js', { items: additions });
      await announce(result);
    } catch (e) {
      console.error('[LAOR cart gifts]', e.message || e);
    } finally {
      busy = false;
      if (dirty) {
        dirty = false;
        queue(null);
      }
    }
  }

  function queue(cart) {
    pending = cart || pending;
    clearTimeout(timer);
    timer = setTimeout(() => {
      const next = pending;
      pending = null;
      settle(next);
    }, 250);
  }

  /* "+ הוסיפי חזייה": one more unit of the newest counted line */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest && e.target.closest('[data-lcg-add]');
    if (!btn || btn.disabled) return;
    e.preventDefault();
    btn.disabled = true;
    btn.setAttribute('aria-busy', 'true');
    try {
      const qty = (parseInt(btn.dataset.qty, 10) || 0) + 1;
      const result = await post('/cart/change.js', { id: btn.dataset.key, quantity: qty });
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'laor-cart-gifts', cartData: result, sections: result.sections });
      }
      document.dispatchEvent(new CustomEvent('cart:update', { detail: result }));
    } catch (err) {
      console.error('[LAOR cart gifts]', err.message || err);
      btn.disabled = false;
      btn.removeAttribute('aria-busy');
    }
  });

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => queue(cartOf(event)));
  }
  document.addEventListener('cart:update', (e) => queue(cartOf(e.detail)));

  /* A cart that arrived out of step (the Liquid says so) is settled once on load */
  function boot() {
    const root = document.querySelector(ROOT);
    if (root && root.dataset.needsSync === 'true') queue(null);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  window.LaorCartGifts = { settle: () => queue(null) };
})();
